import { Parser, Token, type StringToken } from '@parser';
import { iString, type LocaleId } from '@strings';
import { CadetLexer } from './CadetLexer';
import * as TT from './CadetTokens';
import { Item } from './Item';
import { Prologue } from './Prologue';
import { GroupStatement } from './Statements';
import { Unparser } from './Unparser';
import { canonicalizeLocale, isWellFormedLocale } from './i18nLocale';
import { parseItem } from './parseItem';

interface ContainmentContext {
  parent?: GroupStatement;
}

export class CadetParser extends Parser {
  prologue: Prologue | undefined;
  items: Item[] = [];
  containmentStack: ContainmentContext[] = [];

  get containmentContext(): ContainmentContext | undefined {
    return this.containmentStack[this.containmentStack.length - 1];
  }

  pushContainmentContext(context: ContainmentContext): void {
    this.containmentStack.push(context);
  }

  popContainmentContext(): ContainmentContext | undefined {
    return this.containmentStack.pop();
  }

  /**
   * Instance-level parseItem to avoid requiring parseItem module in Statements.
   */
  parseItem(): Item | undefined {
    return parseItem(this);
  }

  get currentParent(): GroupStatement | undefined {
    return this.containmentContext?.parent;
  }

  constructor(textOrLexer: string | CadetLexer) {
    const lexer =
      typeof textOrLexer === 'string' ? new CadetLexer(textOrLexer) : textOrLexer;
    super(lexer);

    try {
      this.prologue = this.expectPrologue();

      this.consumeBreaks();

      while (!this.isEOT) {
        const item = this.parseItem();
        if (item) {
          this.items.push(item);
        }
      }
    } catch {
      return;
    }
  }

  break(): boolean {
    if (this.isEOT) {
      return true;
    }
    return this.withSavedPosition(commit => {
      if (this.consumeToken(TT.BREAK) === undefined) {
        return false;
      }
      commit();
      return true;
    });
  }

  identifier(): StringToken | undefined {
    const token = this.consumeToken(TT.ID);
    if (token === undefined) {
      return undefined;
    }
    return token as StringToken;
  }

  classIdentifier(): StringToken | undefined {
    const token = this.consumeToken(TT.CLASS);
    if (token === undefined) {
      return undefined;
    }
    return token as StringToken;
  }

  arrow(): StringToken | undefined {
    const token = this.consumeToken(TT.ARROW);
    if (token === undefined) {
      return undefined;
    }
    return token as StringToken;
  }

  hasArrowOnLine(): boolean {
    for (let i = this.pos; i < this.tokens.length; i++) {
      const t = this.tokens[i];
      if (t.type === TT.BREAK || t.type === TT.RBRACE) break;
      if (t.type === TT.ARROW) return true;
    }
    return false;
  }

  string(): StringToken | undefined {
    return this.withSavedPosition(commit => {
      const token = this.consumeToken(TT.STRING);
      if (token === undefined) {
        return undefined;
      }
      if (typeof token.value !== 'string') {
        return undefined;
      }
      commit();
      return token as StringToken;
    });
  }

  // Parse an IString literal used in user-facing contexts (titles, notes).
  // Grammar: one or more segments of <JSONString>~<locale>
  // A plain <JSONString> (no ~locale) is accepted and returned unchanged.
  // Deterministic serialization rules applied to token.text:
  //   - Single default-locale (en-US) => emit plain JSON string only
  //   - Single non-default => emit "text"~locale
  //   - Multiple => default first if present, others alphabetical
  iString(): StringToken | undefined {
    return this.withSavedPosition(commit => {
      const first = this.string();
      if (!first) return undefined;

      // Look ahead for optional breaks then a tilde, but don't consume breaks unless a tilde actually follows.
      const tilde = this.withSavedPosition(commitTilde => {
        this.consumeBreaks();
        const t = this.consumeToken(TT.TILDE);
        if (!t) {
          return undefined; // rollback preserves original break/newline
        }
        commitTilde();
        return t;
      });
      if (!tilde) {
        // Plain string only -> construct IString single default variant
        const single = iString(first.value);
        (first as unknown as { value: unknown }).value = single as unknown;
        commit();
        return first;
      }
      const localeTok = this.consumeToken(TT.LOCALE);
      if (!localeTok) {
        throw this.error('Expected locale after ~', tilde.start);
      }
      const rawLoc = localeTok.text;
      if (!isWellFormedLocale(rawLoc)) {
        throw this.error(`Invalid locale tag: ${rawLoc}`, localeTok.start);
      }
      const firstLocale = canonicalizeLocale(rawLoc);
      const variants: Record<string, string> = { [firstLocale]: first.value };

      // Parse additional segments greedily
      for (;;) {
        const seg = this.withSavedPosition(commitSeg => {
          this.consumeBreaks();
          const segStr = this.string();
          if (!segStr) return undefined;
          this.consumeBreaks();
          const segTilde = this.consumeToken(TT.TILDE);
          if (!segTilde) {
            throw this.error('Expected ~locale after IString segment', segStr.start);
          }
          const segLocTok = this.consumeToken(TT.LOCALE);
          if (!segLocTok) {
            throw this.error('Expected locale after ~', segTilde.start);
          }
          const raw = segLocTok.text;
          if (!isWellFormedLocale(raw)) {
            throw this.error(`Invalid locale tag: ${raw}`, segLocTok.start);
          }
          const can = canonicalizeLocale(raw);
          if (Object.prototype.hasOwnProperty.call(variants, can)) {
            throw this.error(`Duplicate locale variant: ${can}`, segLocTok.start);
          }
          variants[can] = segStr.value;
          commitSeg();
          return true;
        });
        if (!seg) break;
      }

      // Build deterministic text representation
      const DEFAULT = 'en-US';
      const entries = Object.entries(variants);
      const def = entries.find(([l]) => l === DEFAULT);
      const others = entries
        .filter(([l]) => l !== DEFAULT)
        .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
      const ordered: [string, string][] = [];
      if (def) ordered.push(def);
      ordered.push(...others);
      let text: string;
      if (ordered.length === 1 && def) {
        text = JSON.stringify(def[1]);
      } else {
        text = ordered.map(([loc, str]) => `${JSON.stringify(str)}~${loc}`).join(' ');
      }
      (first as unknown as { text: string }).text = text;
      const istring = iString(new Map<LocaleId, string>(ordered as [LocaleId, string][]));
      (first as unknown as { value: unknown }).value = istring as unknown;
      commit();
      return first;
    });
  }

  comma(): Token | undefined {
    return this.consumeToken(TT.COMMA);
  }

  lBracket(): Token | undefined {
    return this.consumeToken(TT.LBRACKET);
  }

  rBracket(): Token | undefined {
    return this.consumeToken(TT.RBRACKET);
  }

  lParen(): Token | undefined {
    return this.consumeToken(TT.LPAREN);
  }

  rParen(): Token | undefined {
    return this.consumeToken(TT.RPAREN);
  }

  // Ignore breaks before and after the brace
  lBrace(): Token | undefined {
    if (this.current.type === TT.BREAK) {
      this.consume();
    }
    const brace = this.consumeToken(TT.LBRACE);
    if (this.current.type === TT.BREAK) {
      this.consume();
    }
    return brace;
  }

  // Ignore breaks before and after the brace
  rBrace(): Token | undefined {
    if (this.current.type === TT.BREAK) {
      this.consume();
    }
    const brace = this.consumeToken(TT.RBRACE);
    return brace;
  }

  colon(): Token | undefined {
    return this.consumeToken(TT.COLON);
  }

  private expectPrologue(): Prologue {
    if (this.current.type !== TT.PROLOGUE) {
      throw this.error(`Expected prologue, got ${this.current.type}`, this.current.start);
    }

    const prologue = (this.consume() as Token<Prologue>).value;
    this.prologue = prologue;
    return prologue;
  }

  // Consumes all breaks until the next non-break token
  // Returns true if any breaks were consumed
  consumeBreaks(): boolean {
    let foundBreak = false;
    while (!this.isEOT) {
      if (this.current.type === TT.BREAK) {
        this.consume();
        foundBreak = true;
      } else {
        break;
      }
    }
    return foundBreak;
  }

  // Consumes all tokens until the next break token
  consumeUntilBreak(): void {
    while (!this.isEOT) {
      const type = this.current.type;
      this.consume();
      if (type === 'BREAK') {
        return;
      }
    }
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      prologue: this.prologue?.toJSON(opts),
      items: this.items.map(i => i.toJSON(opts))
    };
  }

  unparse(opts?: { indentUnit?: string; compact?: boolean }): string {
    const { indentUnit, compact } = {
      indentUnit: '    ',
      compact: false,
      ...opts
    };

    const u = new Unparser(indentUnit);
    const prologue = this.prologue;
    if (prologue) {
      prologue.unparse(u);
      u.addLeft(';', '');
      u.endLine();
    }
    for (const [index, item] of this.items.entries()) {
      item.unparse(u);
      if (index < this.items.length - 1) {
        if (!item.selfTerminating) {
          u.addLeft(';', '');
        }
        u.endLine();
      }
    }
    return compact ? u.compact() : u.pretty();
  }
}
